import type { Product } from '@/domain/Product';
import type {
  MediaGalleryProps,
  MediaGalleryItemType
} from '@/widgets/components/controls/MediaGallery/MediaGallery.tsx';

/** Values based on ECAPI media type data */
const SUPPORTED_MEDIA_TYPES: MediaGalleryItemType[] = ['IMAGE', 'VIDEO'];

export function getProductMediaForGallery({
  media,
  mainImage,
  name: productName
}: Product): MediaGalleryProps['media'] {
  const mediaItems = media.map((item) => ({
    type: getMediaTypeFromMime(item.type) as MediaGalleryItemType,
    url: item.url,
    alt: item.altText ?? ''
    // poster: item.type === 'VIDEO' ? '' : undefined // TODO - waiting for ECAPI to support video thumbnails
  }));

  const usable = mediaItems.filter(
    (i) => SUPPORTED_MEDIA_TYPES.indexOf(i.type) !== -1
  );

  if (usable.length === 0) {
    // fallback to the main image
    usable.push({
      type: 'IMAGE' as MediaGalleryItemType,
      url: mainImage.url,
      alt: mainImage.alt ?? productName ?? ''
      // poster: undefined
    });
  }

  return {
    thumbnails: usable.map((item) => ({
      ...item,
      url:
        item.type === 'IMAGE'
          ? getMediaRelativeURLWithPolicyParams(item.url, 'small', 192)
          : item.url
    })),
    items: usable.map((item) => ({
      ...item,
      url:
        item.type === 'IMAGE'
          ? getMediaRelativeURLWithPolicyParams(item.url, 'medium', 800)
          : item.url
    })),
    hires: usable.map((item) => ({
      ...item,
      url:
        item.type === 'IMAGE'
          ? getMediaRelativeURLWithPolicyParams(item.url, 'large', 2000)
          : item.url
    }))
  };
}

export function getMediaRelativeURLWithPolicyParams(
  fileUrl: string,
  policy: 'small' | 'medium' | 'large',
  width: number,
  density?: 1 | 2
) {
  const urlParams = new URLSearchParams();
  urlParams.set('impolicy', policy);
  urlParams.set('imwidth', width.toString());
  urlParams.set('imdensity', (density ?? 1).toString());
  return `${fileUrl}?${urlParams.toString()}`;
}

export function getMediaTypeFromMime(
  mime: string
): MediaGalleryItemType | 'UNKNOWN' {
  if (mime.toLowerCase() === 'image') return 'IMAGE';
  if (mime.toLowerCase() === 'video') return 'VIDEO';
  return 'UNKNOWN';
}
